import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import React from "react";

import { b2cPolicies, loginRequestScopes } from "../../app/configs/appConfig";
import {
  claimRestart,
  clearRestartPending,
  isRestartError,
} from "./signInRestart";

export const MsalEventsHandler: React.FC = () => {
  /**
   * useMsal is hook that returns the PublicClientApplication instance,
   * an array of all accounts currently signed in and an inProgress value
   * that tells you what msal is currently doing. For more, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
   */
  const { instance: msalInstance, inProgress } = useMsal();
  // Set when B2C sent the person back because they left a step (Cancel on
  // "Forgot your password?" = AADB2C90091, the reset link = AADB2C90118).
  // The sign-in redirect starts once MSAL has finished handling the redirect
  // (starting it from inside the event would fail with
  // "interaction_in_progress").
  const [restartSignIn, setRestartSignIn] = React.useState(false);

  /**
   * Using the event API, you can register an event callback that will do something when an event is emitted.
   * When registering an event callback in a React component you will need to make sure you do 2 things.
   * 1) The callback is registered only once
   * 2) The callback is unregistered before the component unmounts.
   * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/events.md
   */
  React.useEffect(() => {
    const callbackId = msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_FAILURE) {
        if (isRestartError(event.error) && claimRestart()) {
          setRestartSignIn(true);
        }
      }

      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        if (event?.payload) {
          /**
           * We need to reject id tokens that were not issued with the default sign-in policy.
           * "acr" claim in the token tells us what policy is used (NOTE: for new policies (v2.0), use "tfp" instead of "acr").
           * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
           */
          const policyTypeKey = "tfp";

          // @ts-ignore
          const policy = (event.payload as AuthenticationResult).idTokenClaims[
            policyTypeKey
          ];
          console.info("MainView | policy", { policy });

          // if (policy === b2cPolicies.names.forgotPassword) {
          //   window.alert(
          //     "Password has been reset successfully. \nPlease sign-in with your new password.",
          //   );
          //   return msalInstance.logout();
          // } else if (policy === b2cPolicies.names.editProfile) {
          //   window.alert(
          //     "Profile has been edited successfully. \nPlease sign-in again.",
          //   );
          //   return msalInstance.logout();
          // }
        }
      }
    });

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, [msalInstance]);

  React.useEffect(() => {
    if (!restartSignIn || inProgress !== InteractionStatus.None) {
      return;
    }
    setRestartSignIn(false);
    msalInstance
      .loginRedirect({
        ...loginRequestScopes,
        authority: b2cPolicies.authorities.signUpSignIn.authority,
      })
      .catch((e) => {
        // The sign-in error screen (AuthenticatedMainView) offers
        // "Back to sign in" when this didn't work.
        console.error("MsalEventsHandler | sign-in restart failed", e);
        clearRestartPending();
      });
  }, [restartSignIn, inProgress, msalInstance]);

  return null;
};
